import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ExternalLink, BookOpen, Bookmark } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEO from "@/components/SEO";
import { SITE_URL as BASE } from "@/lib/site";

export default function ResearchDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    api.get(`/research/${id}`).then((r) => setP(r.data)).catch(() => setErr(true));
    try {
      const b = JSON.parse(localStorage.getItem("iocri_bookmarks") || "[]");
      setBookmarked(b.includes(id));
    } catch {}
  }, [id]);

  const toggleBookmark = () => {
    try {
      const b = JSON.parse(localStorage.getItem("iocri_bookmarks") || "[]");
      const next = b.includes(id) ? b.filter((x) => x !== id) : [...b, id];
      localStorage.setItem("iocri_bookmarks", JSON.stringify(next));
      setBookmarked(!bookmarked);
      setToast(bookmarked ? "Removed from Research Library" : "Saved to Research Library");
      setTimeout(() => setToast(""), 2200);
    } catch {}
  };

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-white/60">Brief not found. <Link to="/research" className="text-gold underline">Back to library</Link>.</div>;
  if (!p) return <div className="max-w-3xl mx-auto px-6 py-24 text-white/60">Loading…</div>;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": p.title,
    "abstract": p.abstract,
    "datePublished": String(p.year),
    "author": (p.authors || []).map((n) => ({ "@type": "Organization", name: n })),
    "publisher": { "@type": "Organization", name: "International Organized Crime Research Institute", url: BASE },
    "keywords": p.category,
    "url": `${BASE}/research/${p.id}`,
    "isAccessibleForFree": true,
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="research-detail-page">
      <SEO title={p.title} description={p.abstract.slice(0, 200)} path={`/research/${p.id}`} image="institute" type="article" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Link to="/research" data-testid="back-to-research" className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm">
        <ArrowLeft className="h-4 w-4" /> Back to Research Library
      </Link>

      <div className="mt-6 text-xs font-mono tracking-widest text-gold">
        {(p.type || "Research").toUpperCase()} · {p.category.toUpperCase()} · {p.year}
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-white mt-3 text-balance leading-tight">{p.title}</h1>
      <div className="text-white/60 font-mono text-sm mt-4">{(p.authors || []).join(" · ")}</div>

      <div className="divider-gold my-10" />

      <Card className="bg-obsidian-soft border-white/5 rounded-2xl">
        <CardContent className="p-8">
          <div className="text-gold font-mono text-xs tracking-widest">SUMMARY</div>
          <p className="mt-3 text-white/80 leading-relaxed">{p.abstract}</p>
        </CardContent>
      </Card>

      {p.sources && p.sources.length > 0 && (
        <section id="sources" className="mt-10" data-testid="brief-sources">
          <div className="text-gold font-mono text-xs tracking-widest">SOURCES &amp; REFERENCES</div>
          <div className="mt-4 space-y-3">
            {p.sources.map((s, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-obsidian-soft p-4" data-testid={`brief-ref-${i + 1}`}>
                <div className="text-xs font-mono text-gold">[{i + 1}]</div>
                <div className="mt-1 text-white">{s.n}</div>
                <a
                  href={s.u}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-gold text-xs font-mono tracking-widest hover:text-gold-light"
                  data-testid={`brief-ref-link-${i + 1}`}
                >
                  VIEW SOURCE <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-10 flex flex-wrap gap-3">
        <Button
          onClick={toggleBookmark}
          data-testid="brief-bookmark-btn"
          className={`rounded-full ${bookmarked ? "bg-gold text-black hover:bg-gold-light" : "bg-transparent border border-gold/40 text-gold hover:bg-gold/10"}`}
        >
          <Bookmark className="mr-2 h-4 w-4" fill={bookmarked ? "currentColor" : "none"} />
          {bookmarked ? "Saved" : "Save to Library"}
        </Button>
        <Link to="/research">
          <Button variant="outline" className="rounded-full border-white/20 bg-transparent hover:bg-white/5 text-white">
            <BookOpen className="mr-2 h-4 w-4" /> More Briefs
          </Button>
        </Link>
      </div>

      {toast && (
        <div data-testid="bookmark-toast" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] px-5 py-3 rounded-full bg-gold text-black font-mono text-xs tracking-widest shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
